import React, { forwardRef, useCallback } from 'react';
import { Input } from '@crisellevaldez/speculo-ui';
import { stripUrlPrefix } from '../../utils/urlUtils';

type HttpsUrlInputProps = Omit<React.ComponentPropsWithoutRef<typeof Input>, 'startIcon' | 'type'>;

/**
 * Web address input with a fixed, non-editable `https://` prefix. Any
 * `https://`, `http://` or `www.` the user types or pastes is stripped as they
 * go, and a saved full URL loads showing only its domain/path. Pair it with a
 * schema that runs `toHttpsUrl` so the submitted value is always
 * `https://domain.com`.
 */
export const HttpsUrlInput = forwardRef<HTMLInputElement, HttpsUrlInputProps>(
  ({ onChange, className, ...props }, ref) => {
    const setRef = useCallback(
      (el: HTMLInputElement | null) => {
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
        // Runs after react-hook-form writes the saved value into the element.
        if (el) el.value = stripUrlPrefix(el.value);
      },
      [ref]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = stripUrlPrefix(e.target.value);
      if (cleaned !== e.target.value) e.target.value = cleaned;
      onChange?.(e);
    };

    return (
      <Input
        {...props}
        ref={setRef}
        type="text"
        inputMode="url"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        startIcon={<span className="text-xs select-none">https://</span>}
        onChange={handleChange}
        className={`pl-16 ${className ?? ''}`}
      />
    );
  }
);

HttpsUrlInput.displayName = 'HttpsUrlInput';
